// src/utils/setupAdminAuth.js
//
// /api/admin/* 로 나가는 모든 요청에 자동으로 Authorization: Bearer <토큰>
// 헤더를 부착하고, 401 응답 시 토큰을 비우고 /admin/login 으로 리다이렉트한다.
//
// - axios: request/response 인터셉터
// - window.fetch: 한 번만 monkey-patch (이중 패치 방지 가드 포함)
//
// 호출은 App.js 등 진입점에서 1회. 부수효과만 있고 export 없음.

import axios from "axios";
import { getAdminToken, clearAdminToken } from "./auth";

const ADMIN_URL_RE = /\/api\/admin(\/|$)|\/api\/eveningclass\/admin(\/|$)/;

const isAdminUrl = (url) => {
  if (!url) return false;
  try {
    const u = String(url);
    return ADMIN_URL_RE.test(u);
  } catch {
    return false;
  }
};

const handleUnauthorized = () => {
  clearAdminToken();
  // 무한 리다이렉트 방지
  if (typeof window === "undefined") return;
  const path = window.location.pathname || "";
  if (path.startsWith("/admin/login")) return;
  if (!path.startsWith("/admin")) return; // 어드민 영역 밖이면 가만히 둠
  window.location.replace("/admin/login");
};

// ─── axios ─────────────────────────────────────────
axios.interceptors.request.use((config) => {
  if (isAdminUrl(config?.url)) {
    const token = getAdminToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url;
    if (err?.response?.status === 401 && isAdminUrl(url)) {
      handleUnauthorized();
    }
    return Promise.reject(err);
  }
);

// ─── window.fetch ───────────────────────────────────
// 이미 한 번 패치되어 있으면 다시 감싸지 않음 (HMR/중복 import 방어)
if (typeof window !== "undefined" && !window.__adminFetchPatched) {
  const origFetch = window.fetch.bind(window);

  const patched = async (resource, init) => {
    let url = "";
    try {
      url = typeof resource === "string"
        ? resource
        : (resource && resource.url) || "";
    } catch {
      url = "";
    }

    if (isAdminUrl(url)) {
      const token = getAdminToken();
      const nextInit = { ...(init || {}) };
      const headers = new Headers(nextInit.headers || {});
      if (token && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      nextInit.headers = headers;
      const res = await origFetch(resource, nextInit);
      if (res.status === 401) handleUnauthorized();
      return res;
    }

    return origFetch(resource, init);
  };

  window.fetch = patched;
  window.__adminFetchPatched = true;
}
